import * as React from "react";

import { ConvexBetterAuthProvider } from "@convex-dev/better-auth/react";
import { loginSchema } from "@repo/shared/auth/users";
import { Button } from "@repo/ui/components/button";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldSeparator,
} from "@repo/ui/components/field";
import { Input } from "@repo/ui/components/input";
import { useForm } from "@tanstack/react-form";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { GalleryVerticalEnd, Loader2Icon } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";

import { authClient, convexAuthClient } from "@/lib/auth-client";
import { authConvexClient } from "@/lib/convex-clients";
import { defaultPathForRole, normalizeRole } from "@/lib/role-access";

export const Route = createFileRoute("/login")({
  head: () => ({ meta: [{ title: "Sign in" }] }),
  component: () => (
    <ConvexBetterAuthProvider client={authConvexClient} authClient={convexAuthClient}>
      <RouteComponent />
    </ConvexBetterAuthProvider>
  ),
  validateSearch: z
    .object({
      continue: z.coerce.boolean().optional(),
      error: z.string().optional(),
      redirect: z.string().optional(),
    })
    .passthrough(),
});

function RouteComponent() {
  const navigate = useNavigate({ from: Route.fullPath });
  const auth = authClient.useSession();
  const { continue: shouldContinue, error: authError, redirect } = Route.useSearch();
  const redirectPath = redirect?.startsWith("/") && !redirect.startsWith("//") ? redirect : null;
  const callbackURL = React.useMemo(
    () => `${window.location.origin}${redirectPath ?? "/"}`,
    [redirectPath],
  );
  const loginURL = React.useMemo(() => `${window.location.origin}/login`, []);
  const socialCallbackURL = React.useMemo(() => {
    const url = new URL(loginURL);
    url.searchParams.set("continue", "1");
    if (redirectPath) url.searchParams.set("redirect", redirectPath);
    return url.toString();
  }, [loginURL, redirectPath]);
  const role = normalizeRole(auth.data?.user.role);
  const continuePath = redirectPath ?? defaultPathForRole(role) ?? "/";
  const [isSubmittingDiscord, setIsSubmittingDiscord] = React.useState(false);
  const [isSigningOut, setIsSigningOut] = React.useState(false);
  const socialErrorMessage = getSocialErrorMessage(authError);

  React.useEffect(() => {
    if (shouldContinue && auth.data && !auth.isPending && !auth.isRefetching) {
      void navigate({ replace: true, to: continuePath });
    }
  }, [auth.data, auth.isPending, auth.isRefetching, continuePath, navigate, shouldContinue]);

  const handleSignOut = async () => {
    setIsSigningOut(true);
    await authClient.signOut();
    window.location.assign("/login");
  };

  const form = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
    validators: {
      onChange: loginSchema,
    },
    onSubmit: async ({ value }) => {
      const { data, error } = await authClient.signIn.email({
        email: value.email.trim().toLowerCase(),
        password: value.password,
        callbackURL,
      });

      if (error) {
        toast.error(error.message ?? "Failed to sign in.");
        return;
      }

      if (data?.url) {
        window.location.assign(data.url);
        return;
      }

      void navigate({ replace: true, to: continuePath });
    },
  });

  const handleDiscordSignIn = async () => {
    setIsSubmittingDiscord(true);

    try {
      await authClient.signIn.social({
        provider: "discord",
        callbackURL: socialCallbackURL,
        errorCallbackURL: loginURL,
      });
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "Failed to continue with Discord.";
      toast.error(message);
      setIsSubmittingDiscord(false);
    }
  };

  if (auth.data && !auth.isPending && !auth.isRefetching) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-background p-6 md:p-10">
        <div className="w-full max-w-sm rounded-lg border bg-card p-6 shadow-sm">
          <div className="flex flex-col gap-4 text-center">
            <div>
              <h1 className="text-xl font-bold">You are already signed in</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                Signed in as {auth.data.user.email}. Sign out if you want to use another account.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              {role !== "pending" ? (
                <Button onClick={() => void navigate({ replace: true, to: continuePath })}>
                  Continue to app
                </Button>
              ) : null}
              <Button
                variant="outline"
                onClick={() => void handleSignOut()}
                disabled={isSigningOut}
              >
                {isSigningOut ? <Loader2Icon className="size-4 animate-spin" /> : null}
                Sign out
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-background p-6 md:p-10">
      <div className="w-full max-w-sm">
        <div className="flex flex-col gap-6">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              event.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <FieldGroup>
              <div className="flex flex-col items-center gap-2 text-center">
                <div className="flex flex-col items-center gap-2 font-medium">
                  <div className="flex size-8 items-center justify-center rounded-md border bg-muted/40">
                    <GalleryVerticalEnd className="size-5" />
                  </div>
                  <span className="sr-only">WorkGrid</span>
                </div>
                <h1 className="text-xl font-bold">Welcome to WorkGrid</h1>
                <FieldDescription>
                  Sign in with your internal account or continue with Discord.
                </FieldDescription>
                {socialErrorMessage ? (
                  <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                    {socialErrorMessage}
                  </p>
                ) : null}
              </div>

              <form.Field name="email">
                {(field) => {
                  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;

                  return (
                    <Field data-invalid={isInvalid}>
                      <FieldLabel htmlFor="email">Email</FieldLabel>
                      <Input
                        id="email"
                        type="email"
                        autoComplete="email"
                        placeholder="demo@work-grid.local"
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(event) => field.handleChange(event.target.value)}
                        required
                      />
                      {isInvalid ? <FieldError errors={field.state.meta.errors} /> : null}
                    </Field>
                  );
                }}
              </form.Field>

              <form.Field name="password">
                {(field) => {
                  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;

                  return (
                    <Field data-invalid={isInvalid}>
                      <FieldLabel htmlFor="password">Password</FieldLabel>
                      <Input
                        id="password"
                        type="password"
                        autoComplete="current-password"
                        placeholder="Enter password"
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(event) => field.handleChange(event.target.value)}
                        required
                      />
                      {isInvalid ? <FieldError errors={field.state.meta.errors} /> : null}
                    </Field>
                  );
                }}
              </form.Field>

              <Field>
                <form.Subscribe
                  selector={(state) => [state.canSubmit, state.isSubmitting] as const}
                >
                  {([canSubmit, isSubmitting]) => (
                    <Button
                      type="submit"
                      disabled={!canSubmit || isSubmitting || isSubmittingDiscord}
                    >
                      {isSubmitting ? <Loader2Icon className="size-4 animate-spin" /> : null}
                      Continue with email
                    </Button>
                  )}
                </form.Subscribe>
              </Field>

              <FieldSeparator>Or</FieldSeparator>

              <Field>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => void handleDiscordSignIn()}
                  disabled={isSubmittingDiscord}
                >
                  {isSubmittingDiscord ? (
                    <Loader2Icon className="size-4 animate-spin" />
                  ) : (
                    <DiscordIcon />
                  )}
                  Continue with Discord
                </Button>
              </Field>
            </FieldGroup>
          </form>
        </div>
      </div>
    </div>
  );
}

const socialErrorMessages: Record<string, string> = {
  account_not_linked:
    "This Discord account is not linked to an allowed WorkGrid user. Try signing in with email first.",
  signup_disabled: "This Discord account is not allowed to sign in.",
  access_denied: "Discord authorization was cancelled.",
  email_not_found: "Discord did not return an email address for this account.",
  invalid_code: "Discord authorization failed. Please try again.",
};

const getSocialErrorMessage = (error?: string) =>
  error
    ? (socialErrorMessages[error] ?? "Discord sign-in failed. Please try again or use email.")
    : null;

function DiscordIcon() {
  return (
    <svg
      className="size-4"
      xmlns="http://www.w3.org/2000/svg"
      width="800px"
      height="800px"
      viewBox="0 -28.5 256 256"
      version="1.1"
      preserveAspectRatio="xMidYMid"
      aria-hidden="true"
    >
      <g>
        <path
          d="M216.856339,16.5966031 C200.285002,8.84328665 182.566144,3.2084988 164.041564,0 C161.766523,4.11318106 159.108624,9.64549908 157.276099,14.0464379 C137.583995,11.0849896 118.072967,11.0849896 98.7430163,14.0464379 C96.9108417,9.64549908 94.1925838,4.11318106 91.8971895,0 C73.3526068,3.2084988 55.6133949,8.86399117 39.0420583,16.6376612 C5.61752293,67.146514 -3.4433191,116.400813 1.08711069,164.955721 C23.2560196,181.510915 44.7403634,191.567697 65.8621325,198.148576 C71.0772151,190.971126 75.7283628,183.341335 79.7352139,175.300261 C72.104019,172.400575 64.7949724,168.822202 57.8887866,164.667963 C59.7209612,163.310589 61.5131304,161.891452 63.2445898,160.431257 C105.36741,180.133187 151.134928,180.133187 192.754523,160.431257 C194.506336,161.891452 196.298154,163.310589 198.110326,164.667963 C191.183787,168.842556 183.854737,172.420929 176.223542,175.320965 C180.230393,183.341335 184.861538,190.991831 190.096624,198.16893 C211.238746,191.588051 232.743023,181.531619 254.911949,164.955721 C260.227747,108.668201 245.831087,59.8662432 216.856339,16.5966031 Z M85.4738752,135.09489 C72.8290281,135.09489 62.4592217,123.290155 62.4592217,108.914901 C62.4592217,94.5396472 72.607595,82.7145587 85.4738752,82.7145587 C98.3405064,82.7145587 108.709962,94.5189427 108.488529,108.914901 C108.508531,123.290155 98.3405064,135.09489 85.4738752,135.09489 Z M170.525237,135.09489 C157.88039,135.09489 147.510584,123.290155 147.510584,108.914901 C147.510584,94.5396472 157.658606,82.7145587 170.525237,82.7145587 C183.391518,82.7145587 193.761324,94.5189427 193.539891,108.914901 C193.539891,123.290155 183.391518,135.09489 170.525237,135.09489 Z"
          fill="#5865F2"
          fillRule="nonzero"
        ></path>
      </g>
    </svg>
  );
}
